/**
 * shapeDetector.js
 * Utility functions for analysing MediaPipe Face Mesh landmarks + blendshapes.
 *
 * Based on MouthShapeCoach.jsx reference — uses both landmark ratios AND
 * MediaPipe blendshape scores (jawOpen, smile, pucker, tongueOut) for
 * more accurate 0–1 match scoring.
 */

// Key landmark indices (MediaPipe Face Mesh canonical model)
const LIP = {
  upperOuter: 13,    // top-centre of upper lip (outer)
  lowerOuter: 14,    // bottom-centre of lower lip (outer)
  leftCorner: 61,    // left mouth corner
  rightCorner: 291,  // right mouth corner
  upperInner: 82,    // top-centre of upper lip (inner)
  lowerInner: 87,    // bottom-centre of lower lip (inner)
  leftEye: 33,       // left eye outer corner (for normalisation)
  rightEye: 263,     // right eye outer corner
};

// Lip contour loops (MediaPipe Face Mesh canonical indices) — used to draw
// the lip-tracking overlay. Each array is an ordered ring around the lips.
export const LIP_OUTER_LOOP = [
  61, 146, 91, 181, 84, 17, 314, 405, 321, 375,
  291, 409, 270, 269, 267, 0, 37, 39, 40, 185,
];
export const LIP_INNER_LOOP = [
  78, 95, 88, 178, 87, 14, 317, 402, 318, 324,
  308, 415, 310, 311, 312, 13, 82, 81, 80, 191,
];

/** Euclidean distance between two { x, y } landmark objects. */
function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// ─── 1. COMPUTE MOUTH METRICS ────────────────────────────────────────────────
/**
 * Analyse the current lip landmarks + blendshapes and return derived metrics.
 * @param {Array} lm – full 478-landmark array from MediaPipe
 * @param {Array|null} blendshapes – blendshape categories array from MediaPipe
 * @returns {object|null} metrics for scoring
 */
export function computeMouthMetrics(lm, blendshapes) {
  if (!lm || lm.length < 300) return null;

  const openHeight = dist(lm[LIP.upperOuter], lm[LIP.lowerOuter]);
  const mouthWidth = dist(lm[LIP.leftCorner], lm[LIP.rightCorner]);
  const innerOpen  = dist(lm[LIP.upperInner], lm[LIP.lowerInner]);
  const faceWidth  = dist(lm[LIP.leftEye], lm[LIP.rightEye]) || 0.001;

  if (mouthWidth < 0.005) return null;

  // Build blendshapes map
  const bs = {};
  if (blendshapes) {
    blendshapes.forEach(item => {
      bs[item.categoryName] = item.score;
    });
  }

  return {
    visible: true,
    openRatio: openHeight / mouthWidth,
    normalizedMouthWidth: mouthWidth / faceWidth,
    innerOpen: Math.round(innerOpen * 1000) / 1000,
    // Lip ratio (kept for backward compatibility)
    ratio: mouthWidth > 0.001 ? Math.round((mouthWidth / openHeight) * 100) / 100 : 99,
    // Blendshape scores (0–1), null if unavailable
    jawOpen:    bs.jawOpen ?? null,
    smileLeft:  bs.mouthSmileLeft ?? null,
    smileRight: bs.mouthSmileRight ?? null,
    pucker:     bs.mouthPucker ?? bs.mouthFunnel ?? null,
    tongueOut:  bs.tongueOut ?? null,
  };
}

// ─── 1b. WHAT THIS DETECTOR CANNOT JUDGE ─────────────────────────────────────
/**
 * Face Mesh tracks the face. It has no tongue landmarks at all — the one tongue
 * signal MediaPipe gives is a single `tongueOut` blendshape, which says how far
 * the tongue is out and nothing about where it points. So:
 *
 *  - `tongue_left` / `tongue_right` cannot be told apart from each other, or from
 *    a straight protrusion.
 *  - `tongue_up` has no signal either. The score it returns is derived from jaw
 *    opening, which is a proxy wearing a tongue's name: a child who simply opens
 *    their mouth scores the same as one who lifts the tip to the alveolar ridge.
 *
 * A meter that cannot measure should not be shown. This is exported so the
 * screens decide that from one place rather than each keeping its own list.
 */
export const UNSCORABLE_SHAPES = ['tongue_left', 'tongue_right', 'tongue_up'];

/**
 * @param {string} shapeType
 * @param {object|null} [metrics] – output of computeMouthMetrics(), when known
 * @returns {boolean} whether a match score for this shape means anything
 */
export function canScoreShape(shapeType, metrics) {
  if (UNSCORABLE_SHAPES.includes(shapeType)) return false;
  // Protrusion is real, but only where the blendshape arrives. Without it there
  // is no tongue signal whatsoever, so the shape is unscorable on this device.
  if (shapeType === 'tongue_out') return metrics ? metrics.tongueOut != null : true;
  return true;
}

// ─── 2. COMPUTE VISUAL MATCH SCORE (0–1) ─────────────────────────────────────
/**
 * Score how well the detected face matches the target shape.
 * @param {string} shapeType – target_shape_type from exercise
 * @param {object} metrics – output of computeMouthMetrics()
 * @returns {number} 0–1 (1 = perfect match)
 */
export function computeMatchScore(shapeType, metrics) {
  if (!metrics?.visible) return 0;

  const { jawOpen, smileLeft, smileRight, pucker, tongueOut, openRatio, normalizedMouthWidth } = metrics;

  // ── OPEN (aah / आ) ──────────────────────────────────────────────
  if (shapeType === 'open') {
    if (jawOpen != null) return Math.min(jawOpen / 0.55, 1);
    const threshold = 0.28;
    return Math.min(openRatio / threshold, 1) * 0.85 + (openRatio > threshold * 0.4 ? 0.15 : 0);
  }

  // ── WIDE / SMILE (see / ई) ─────────────────────────────────────
  if (shapeType === 'wide') {
    if (smileLeft != null) {
      const smileScore = (smileLeft + smileRight) / 2;
      const notOpen = jawOpen != null ? Math.max(0, 1 - jawOpen / 0.3) : 1;
      return Math.min(smileScore / 0.45, 1) * 0.75 + notOpen * 0.25;
    }
    const wide = Math.min(Math.max((normalizedMouthWidth - 0.55) / 0.18, 0), 1);
    const flat = openRatio < 0.12 ? 1 : Math.max(0, 1 - (openRatio - 0.12) / 0.1);
    return Math.max(0, wide * 0.65 + flat * 0.35);
  }

  // ── ROUND (moo / ऊ) ────────────────────────────────────────────
  if (shapeType === 'round') {
    if (pucker != null) return Math.min(pucker / 0.35, 1);
    const puckered = Math.max(0, 1 - Math.max(0, normalizedMouthWidth - 0.42) / 0.18);
    return Math.max(0, puckered * 0.55 + Math.min(openRatio / 0.14, 1) * 0.45);
  }

  // ── TONGUE OUT (protrusion) ────────────────────────────────────
  // computeMouthMetrics has collected `tongueOut` all along and nothing read it,
  // so this shape fell through to `return 0` and its meter never moved.
  if (shapeType === 'tongue_out') {
    if (tongueOut == null) return 0;        // canScoreShape() hides the meter
    return Math.min(tongueOut / 0.35, 1);
  }

  // ── TONGUE UP (la / ल) ─────────────────────────────────────────
  // A jaw-opening proxy, kept as it was for the practice exercises that have
  // always used it. canScoreShape() reports it as unscorable, so the assessment
  // screens do not present this number as a measurement of the tongue.
  if (shapeType === 'tongue_up') {
    const jawScore = jawOpen != null ? jawOpen : openRatio;
    return jawScore > 0.13 ? 0.55 : 0.2;
  }

  // ── CLOSED / LIP PRESS (mmm / म) ──────────────────────────────
  if (shapeType === 'closed') {
    // Lips pressed = jaw NOT open + mouth vertical opening is small relative to width
    const jawClosed = jawOpen != null ? Math.max(0, 1 - jawOpen / 0.15) : 1;
    const lipsTight = openRatio < 0.15 ? 1 : Math.max(0, 1 - (openRatio - 0.15) / 0.15);
    return jawClosed * 0.5 + lipsTight * 0.5;
  }

  // ── PUFF CHEEKS (phoo / फू) ────────────────────────────────────
  if (shapeType === 'puff') {
    // Similar to closed — lips sealed, MediaPipe can't see air
    const jawClosed = jawOpen != null ? Math.max(0, 1 - jawOpen / 0.15) : 1;
    const lipsTight = openRatio < 0.18 ? 1 : Math.max(0, 1 - (openRatio - 0.18) / 0.1);
    return jawClosed * 0.4 + lipsTight * 0.6;
  }

  return 0;
}

// ─── 3. BACKWARD COMPAT — boolean check (used if needed) ─────────────────────
/**
 * @deprecated Use computeMatchScore() instead for 0–1 scoring.
 */
export function checkShapeMatch(detected, exercise) {
  if (!detected || !exercise) return false;
  return computeMatchScore(exercise.target_shape_type, detected) > 0.72;
}

// ─── 4. HINT GENERATOR ──────────────────────────────────────────────────────
/**
 * Return a guidance string based on current score.
 * @param {string} shapeType – target_shape_type
 * @param {number} score – visual match score 0–1
 * @returns {string}
 */
export function getHint(shapeType, score) {
  if (score > 0.8)  return '👍 Good — keep holding!';
  if (score > 0.6)  return '🔥 Almost there!';

  switch (shapeType) {
    case 'open':      return 'Drop your jaw more — open wide';
    case 'wide':      return 'Spread lips wide, keep jaw relaxed';
    case 'round':     return 'Push lips forward into a small circle';
    case 'tongue_out': return 'Stick your tongue straight out';
    case 'tongue_up': return 'Touch tongue tip just behind top teeth';
    case 'closed':    return 'Press both lips together firmly';
    case 'puff':      return 'Fill cheeks with air, seal lips tight';
    default:          return 'Relax and hold the shape';
  }
}
